/**
 * Making Custom Text Field for the purpose of usability accross multiple components
 */
import React, { ChangeEvent } from 'react';
import { FormErrorMessage } from '../../Common/FormErrorMessage';
import { useTranslation } from 'react-i18next';
import { TextField } from '@material-ui/core';
import { FormField, validateFormFieldValue, getValueFromEvent } from '../../../utils/formValidationHelper';
import { truncateString } from '../../../utils/helpers';

type ComponentProps = {
  field: FormField;
  classes?: string;
  disabled?: boolean;
  maxLength: number;
  minLength: number;
  size: 'small' | 'medium' | undefined;
  multiline?: boolean;
  rows?: string | number;
  label?: string;
  keyDownHandler?: (event: React.KeyboardEvent) => void;
  executeOnChange?: () => void;
  setStateFn: (field: FormField) => void;
  errorMessage?: string
  ariaLabel?: string
  required?: boolean
};

export const TextFieldWithValidation: React.FC<ComponentProps> = ({
  field,
  classes,
  disabled,
  maxLength,
  minLength,
  size,
  multiline,
  rows,
  label,
  executeOnChange,
  setStateFn,
  keyDownHandler,
  errorMessage,
  ariaLabel,
  required = false
}): JSX.Element => {
  const [t] = useTranslation();

  const fieldChangeHandler = (
    event: ChangeEvent,
    setStateFn: (field: FormField) => void,
    field: FormField,
    maxLimit: number,
    minLimit: number,
  ): void => {
    let value = getValueFromEvent(event);
    const valueLen = value.trim().length;
    if (valueLen > maxLimit) {
      value = truncateString(value, maxLimit, true); // Donot let the value grow beyond max limit
    }
    const validity = validateFormFieldValue(valueLen, minLimit, value);
    setStateFn({
      value: value,
      error: !validity,
      visited: field.visited,
    });
  };

  const handleFieldBlur = (
    event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>,
    setStateFn: (field: FormField) => void,
    field: FormField,
  ): void => {
    setStateFn({
      value: field.value,
      error: field.error,
      visited: true,
    });
  };

  const onChangeHandler = (e: React.ChangeEvent<Element>) => {
    if (executeOnChange) {
      executeOnChange();
    }
    fieldChangeHandler(e, setStateFn, field, maxLength, minLength);
  };

  return (
    <>
      <TextField
        value={field.value}
        classes={{ root: classes }}
        label={label}
        error={field.error && field.visited}
        variant="outlined"
        fullWidth
        onChange={e => {
          onChangeHandler(e);
        }}
        onBlur={e => {
          handleFieldBlur(e, setStateFn, field);
        }}
        onKeyDown={keyDownHandler}
        helperText={field.error && field.visited && <FormErrorMessage message={errorMessage || t('generic_error')}/>}
        size={size}
        multiline={multiline}
        rows={rows}
        disabled={disabled}
        aria-label={ariaLabel+'  '+errorMessage}
        required={required}
        InputLabelProps={{style:{width:"90%"}}}
      />
    </>
  );
};
